/**
 * AdminJotAuditOrgsPage Component
 *
 * Admin page listing all organizations with audit data
 * Clicking an organization navigates to that org's dashboard
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BusinessOutlined as EmptyIcon, Close as CloseIcon,Search as SearchIcon } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { JotAuditPageLayout } from './components/layout';
import { OrganizationRow } from './components/organizations/OrganizationRow';
import { useGetAdminOrganizationsQuery } from './store/adminApi';

export const AdminJotAuditOrgsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');

  // Get pagination and search params from URL
  const page = parseInt(searchParams.get('page') || '1', 10);
  const search = searchParams.get('search') || undefined;

  const { data, isLoading, error, refetch } = useGetAdminOrganizationsQuery({
    page,
    perPage: 20,
    search: search || null,
  });

  const organizations = useMemo(() => data?.organizations || [], [data]);
  const total = data?.total || 0;
  const totalPages = data?.total_pages || 1;
  const hasMore = data?.has_more || false;
  const isEmpty = !isLoading && organizations.length === 0;

  const handleSearch = useCallback(() => {
    const params = new URLSearchParams();
    if (searchInput.trim()) {
      params.set('search', searchInput.trim());
    }
    params.set('page', '1'); // Reset to first page on search
    setSearchParams(params);
  }, [searchInput, setSearchParams]);

  const handleSearchKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    const params = new URLSearchParams();
    params.set('page', '1');
    setSearchParams(params);
  }, [setSearchParams]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('page', newPage.toString());
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleOrgClick = useCallback(
    (organizationId: string) => {
      navigate(`/admin-v2/jotaudit/${organizationId}`);
    },
    [navigate],
  );

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading organizations</AlertTitle>
        {error instanceof Error ? error.message : 'Failed to load organizations'}
      </Alert>
    );
  }

  return (
    <JotAuditPageLayout>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Organizations
          </Typography>
          {!isLoading && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {total} {total === 1 ? 'organization' : 'organizations'} with audit data
            </Typography>
          )}
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small"
          placeholder="Search organizations by name..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch} edge="end">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ maxWidth: 400 }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={isLoading}
        >
          Search
        </Button>
      </Box>

      {/* List Container */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Loading State */}
        {isLoading && organizations.length === 0 ? (
          <Box>
            {Array.from({ length: 5 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  borderBottom: `1px solid ${theme.palette.grey[100]}`,
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Skeleton variant="circular" width={44} height={44} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="40%" height={20} sx={{ mb: 0.5 }} />
                  <Skeleton width="30%" height={16} />
                </Box>
                <Skeleton width={60} height={20} />
                <Skeleton width={60} height={20} />
                <Skeleton width={80} height={24} />
              </Box>
            ))}
          </Box>
        ) : isEmpty ? (
          /* Empty State */
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              px: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: theme.palette.grey[100],
                mb: 2,
              }}
            >
              <EmptyIcon sx={{ fontSize: 32, color: theme.palette.grey[400] }} />
            </Box>
            <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 0.5 }}>
              No organizations found
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}
            >
              Audit data will appear here once organizations have audited notes
            </Typography>
          </Box>
        ) : (
          /* Organization List */
          <Box>
            {organizations.map(org => (
              <OrganizationRow
                key={org.organization_id}
                organization={org}
                onClick={handleOrgClick}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Pagination */}
      {!isEmpty && totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mt: 3,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1 || isLoading}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Page {page} of {totalPages}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages || isLoading || !hasMore}
          >
            Next
          </Button>
        </Box>
      )}
    </JotAuditPageLayout>
  );
};

export default AdminJotAuditOrgsPage;

